import { useQuery } from '@tanstack/react-query';

import { useOrpc } from '@/_shared/hooks/useOrpc';
import { DashboardPanel } from '@/dashboard/components/DashboardPanel';
import { panelError, panelErrorForAssistant } from '@/dashboard/lib/panel-error';
import type { RpcDashboardSpec } from '@/dashboard/lib/to-rpc-spec';
import type { DashboardPanel as DashboardPanelType } from '@/dashboard/types';

export type PanelDataSource =
  | { mode: 'saved'; dashboardId: string }
  | { mode: 'preview'; warehouseId: string; spec: RpcDashboardSpec };

export function PanelWithData({
  source,
  panel,
  params,
  paramsLoading,
  onAskAssistant,
  onFixError,
  keepDataOnRefetch = false,
}: {
  source: PanelDataSource;
  panel: DashboardPanelType;
  params: Record<string, string | string[]>;
  paramsLoading?: boolean;
  /** Toolbar ✨ — attach the panel to the composer. */
  onAskAssistant?: (panel: { id: string; title: string }) => void;
  /** Error CTA — send the canned fix prompt, with the engine's own text when the server sent it. */
  onFixError?: (panel: { id: string; title: string }, error: string) => void;
  /**
   * Standalone dashboards keep the prior rows visible during a same-key background refetch (e.g.
   * window refocus after the 30s staleTime). Left false for the chat preview, which shows a skeleton
   * on refetch so stale rows don't flash while the agent edits the spec (plan §3).
   */
  keepDataOnRefetch?: boolean;
}): React.ReactElement {
  const orpc = useOrpc();

  const previewOpts =
    source.mode === 'preview'
      ? orpc.dashboard.preview.queryOptions({
          input: {
            warehouseId: source.warehouseId,
            spec: {
              parameters: source.spec.parameters,
              panels: [
                {
                  ...panel,
                  sql: panel.sql ?? '',
                  order: panel.order ?? 0,
                },
              ],
            },
            panelId: panel.id,
            params,
          },
        })
      : null;

  const savedOpts =
    source.mode === 'saved'
      ? orpc.dashboard.query.queryOptions({
          input: {
            dashboardId: source.dashboardId,
            panelId: panel.id,
            params,
          },
        })
      : null;

  const queryOpts = previewOpts ?? savedOpts!;
  const { data, isPending, isFetching, error } = useQuery({
    ...queryOpts,
    enabled: !paramsLoading,
  });

  const failure = panelError(error);
  const errorMsg = failure?.text ?? null;
  // Phase 3c: a paused warehouse needs an admin to reconnect it; the assistant cannot fix that. Nor can
  // it repair a removed account (ACT2-1286): the page banner offers Repair instead.
  const reconnect =
    (error as { data?: { reason?: string } } | null)?.data?.reason === 'warehouse_reconnect_required' ||
    failure?.needsRepair === true;
  const loading = isPending;
  // React Query retains `data` across a same-key background refetch, so when the caller opts to keep
  // data we simply don't surface the refetch as "busy" — the panel keeps showing rows instead of a skeleton.
  const refetching = !keepDataOnRefetch && isFetching && !isPending && data !== undefined;

  return (
    <div className={panel.width === 'full' ? 'lg:col-span-2' : 'lg:col-span-1'}>
      <DashboardPanel
        panel={panel}
        data={data ?? null}
        loading={loading}
        refetching={refetching}
        error={errorMsg}
        problem={failure?.problem ?? null}
        onAskAssistant={onAskAssistant ? () => onAskAssistant({ id: panel.id, title: panel.title }) : undefined}
        onFixError={
          onFixError && failure && !reconnect
            ? () => onFixError({ id: panel.id, title: panel.title }, panelErrorForAssistant(failure))
            : undefined
        }
      />
    </div>
  );
}
